import React, { useEffect, useState } from 'react';
import { supabase } from '../../../supabaseClient';
import ctfDataRaw from '../../../data/ctf.json';

export default function CTFTab() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageId, setPageId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showAdvancedJSON, setShowAdvancedJSON] = useState(false);
    const [jsonString, setJsonString] = useState('');
    
    const [formData, setFormData] = useState({
        section: { eyebrow: '', title: '', titleAccent: '', titleEnd: '', subtitle: '', prizesEyebrow: '', prizesTitle: '', ctaTitle: '', ctaSubtitle: '', ctaButtonText: '', ctaButtonLink: '', ctaButton2Text: '', ctaButton2Link: '' },
        hero: { date: '', teamSize: '', prizePool: '', timing: '', difficulty: '' },
        terminal: {},
        interactiveTree: {},
        categories: [],
        prizes: []
    });

    useEffect(() => {
        fetchPageContent();
    }, []);

    const fetchPageContent = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('ctf_page_content')
            .select('*')
            .eq('page_name', 'ctf-page')
            .single();

        if (error && error.code !== 'PGRST116') { 
            if (error.code === '42P01') {
                setError("Table 'ctf_page_content' does not exist in Supabase. Please create it first using the provided SQL.");
            } else {
                setError(error.message);
            }
        } else if (data) {
            setPageId(data.id);
            setFormData(data.data);
            setError(null);
        }
        setLoading(false);
    };

    const handleImportJson = async () => {
        if (!window.confirm("This will import page content from ctf.json. Are you sure?")) return;
        setLoading(true);
        
        try {
            const { error } = await supabase.from('ctf_page_content').upsert([{
                page_name: 'ctf-page',
                data: ctfDataRaw
            }], { onConflict: 'page_name' });
            
            if (error) throw error;
            
            alert('Import successful!');
            fetchPageContent();
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        
        const payloadData = { ...formData };

        if (pageId) {
            const { error: updateError } = await supabase
                .from('ctf_page_content')
                .update({ data: payloadData })
                .eq('id', pageId);
            if (updateError) setError(updateError.message);
        } else {
            const { data, error: insertError } = await supabase
                .from('ctf_page_content')
                .upsert([{ page_name: 'ctf-page', data: payloadData }], { onConflict: 'page_name' })
                .select();
            if (insertError) {
                setError(insertError.message);
            } else if (data && data.length > 0) {
                setPageId(data[0].id);
            }
        }
        
        if (!error) {
            alert('Page content saved successfully!');
            setIsEditing(false);
            fetchPageContent();
        } else {
            setLoading(false);
        }
    };

    // Category helpers
    const addCategory = () => {
        setFormData({
            ...formData,
            categories: [...formData.categories, { icon: 'fa-terminal', title: 'New Category', points: '100 pts', desc: '' }]
        });
    };
    
    const updateCategory = (index, field, value) => {
        const newCats = [...formData.categories];
        newCats[index][field] = value;
        setFormData({ ...formData, categories: newCats });
    };

    const removeCategory = (index) => {
        const newCats = [...formData.categories];
        newCats.splice(index, 1);
        setFormData({ ...formData, categories: newCats });
    };

    // Prize helpers
    const addPrize = () => {
        setFormData({
            ...formData,
            prizes: [...formData.prizes, { rank: '1st', icon: 'fa-trophy', color: '#FFD700', prize: 'Reward' }]
        });
    };
    
    const updatePrize = (index, field, value) => {
        const newPrizes = [...formData.prizes];
        newPrizes[index][field] = value;
        setFormData({ ...formData, prizes: newPrizes });
    };

    const removePrize = (index) => {
        const newPrizes = [...formData.prizes];
        newPrizes.splice(index, 1);
        setFormData({ ...formData, prizes: newPrizes });
    };

    if (!pageId && !loading && !error) {
        return (
            <div className="card shadow-lg border-0 rounded-4 text-center py-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <h4 className="text-white mb-3">No CTF Content Found</h4>
                <p style={{ color: 'rgba(255,255,255,0.5)' }}>Please import the initial content from JSON.</p>
                <div>
                    <button onClick={handleImportJson} className="btn btn-outline-warning">
                        <i className="fa fa-download me-2"></i>Import from JSON
                    </button>
                </div>
            </div>
        );
    }

    if (isEditing) {
        return (
            <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="card-header border-0 bg-transparent py-4 px-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>Edit CTF Content</h4>
                        <button 
                            type="button" 
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => {
                                if (!showAdvancedJSON) {
                                    setJsonString(JSON.stringify(formData, null, 2));
                                }
                                setShowAdvancedJSON(!showAdvancedJSON);
                            }}
                        >
                            <i className="fas fa-code me-2"></i> {showAdvancedJSON ? "Hide Advanced JSON" : "Show Advanced JSON Editor"}
                        </button>
                    </div>
                </div>
                
                <div className="card-body px-4 pb-4 text-white">
                    {error && <div className="alert alert-danger">{error}</div>}
                    
                    <form onSubmit={handleSubmit}>
                        {showAdvancedJSON ? (
                            <div className="col-12 mb-4">
                                <div className="alert alert-warning mb-3">
                                    <strong>Warning:</strong> Be very careful when editing raw JSON. A syntax error will break the layout.
                                </div>
                                <label className="form-label">Raw Data (JSON)</label>
                                <textarea 
                                    className="form-control bg-dark text-warning" 
                                    style={{ border: '1px solid rgba(255,255,255,0.2)', fontFamily: 'monospace' }} 
                                    rows="25"
                                    value={jsonString}
                                    onChange={e => setJsonString(e.target.value)}
                                    onBlur={e => {
                                        try {
                                            const parsed = JSON.parse(e.target.value);
                                            setFormData(parsed);
                                        } catch(err) {
                                            alert("Invalid JSON format! Please fix any missing quotes or commas before saving.");
                                        }
                                    }}
                                ></textarea>
                            </div>
                        ) : (
                            <>
                                <h5 className="mb-3 mt-2" style={{ color: 'var(--eventflow-primary)' }}>Hero Header & CTF Details</h5>
                                <div className="row g-3 mb-4">
                                    <div className="col-md-3">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Eyebrow Text</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.section?.eyebrow || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, eyebrow: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title Start</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.section?.title || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, title: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title Accent (Colored)</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.section?.titleAccent || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, titleAccent: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title End</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.section?.titleEnd || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, titleEnd: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Subtitle / Description</label>
                                        <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" 
                                            value={formData.section?.subtitle || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, subtitle: e.target.value}})} 
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="row g-3 mb-4">
                                    <div className="col-md-2">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Event Date</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.hero?.date || ''} 
                                            onChange={e => setFormData({...formData, hero: {...formData.hero, date: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Team Size</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.hero?.teamSize || ''} 
                                            onChange={e => setFormData({...formData, hero: {...formData.hero, teamSize: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Prize Pool</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.hero?.prizePool || ''} 
                                            onChange={e => setFormData({...formData, hero: {...formData.hero, prizePool: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Timing</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.hero?.timing || ''} 
                                            onChange={e => setFormData({...formData, hero: {...formData.hero, timing: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Difficulty</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.hero?.difficulty || ''} 
                                            onChange={e => setFormData({...formData, hero: {...formData.hero, difficulty: e.target.value}})} 
                                        />
                                    </div>
                                </div>
                                
                                <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                
                                <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                                    <h5 className="m-0" style={{ color: 'var(--eventflow-primary)' }}>Challenge Categories</h5>
                                    <button type="button" className="btn btn-sm btn-outline-info" onClick={addCategory}>+ Add Category</button>
                                </div>
                                
                                {formData.categories.map((cat, idx) => (
                                    <div className="card bg-transparent mb-3" style={{ border: '1px dashed rgba(255,255,255,0.2)' }} key={idx}>
                                        <div className="card-body p-3">
                                            <div className="d-flex justify-content-between mb-2">
                                                <h6 className="m-0 text-white-50">Category {idx + 1}</h6>
                                                <button type="button" className="btn btn-sm btn-danger py-0 px-2" onClick={() => removeCategory(idx)}><i className="fas fa-trash"></i></button>
                                            </div>
                                            <div className="row g-2">
                                                <div className="col-md-2">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Icon (fa-xxx)</label>
                                                    <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                        value={cat.icon} onChange={e => updateCategory(idx, 'icon', e.target.value)} />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title</label>
                                                    <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                        value={cat.title} onChange={e => updateCategory(idx, 'title', e.target.value)} />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Points Label</label>
                                                    <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                        value={cat.points} onChange={e => updateCategory(idx, 'points', e.target.value)} />
                                                </div>
                                                <div className="col-12">
                                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Description</label>
                                                    <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                        value={cat.desc} onChange={e => updateCategory(idx, 'desc', e.target.value)} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                
                                <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                                    <h5 className="m-0" style={{ color: 'var(--eventflow-primary)' }}>Prizes</h5>
                                    <button type="button" className="btn btn-sm btn-outline-info" onClick={addPrize}>+ Add Prize</button>
                                </div>
                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Prizes Section Eyebrow</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.section?.prizesEyebrow || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, prizesEyebrow: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Prizes Section Title</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.section?.prizesTitle || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, prizesTitle: e.target.value}})} 
                                        />
                                    </div>
                                </div>
                                
                                {formData.prizes.map((p, idx) => (
                                    <div className="row g-2 mb-2 align-items-end" key={idx}>
                                        <div className="col-md-2">
                                            <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Rank</label>
                                            <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                value={p.rank} onChange={e => updatePrize(idx, 'rank', e.target.value)} />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Icon</label>
                                            <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                value={p.icon} onChange={e => updatePrize(idx, 'icon', e.target.value)} />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Hex Color</label>
                                            <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                value={p.color} onChange={e => updatePrize(idx, 'color', e.target.value)} />
                                        </div>
                                        <div className="col-md-5">
                                            <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Prize Text</label>
                                            <input type="text" className="form-control bg-transparent text-white form-control-sm" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                                value={p.prize} onChange={e => updatePrize(idx, 'prize', e.target.value)} />
                                        </div>
                                        <div className="col-md-1 text-end">
                                            <button type="button" className="btn btn-sm btn-danger mb-1" onClick={() => removePrize(idx)}><i className="fas fa-trash"></i></button>
                                        </div>
                                    </div>
                                ))}

                                <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                
                                <h5 className="mb-3 mt-4" style={{ color: 'var(--eventflow-primary)' }}>Call To Action Area</h5>
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.section?.ctaTitle || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, ctaTitle: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Description</label>
                                        <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" 
                                            value={formData.section?.ctaSubtitle || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, ctaSubtitle: e.target.value}})} 
                                        ></textarea>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Primary Button Text</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.section?.ctaButtonText || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, ctaButtonText: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Primary Button Link</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.section?.ctaButtonLink || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, ctaButtonLink: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Secondary Button Text</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.section?.ctaButton2Text || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, ctaButton2Text: e.target.value}})} 
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Secondary Button Link</label>
                                        <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                            value={formData.section?.ctaButton2Link || ''} 
                                            onChange={e => setFormData({...formData, section: {...formData.section, ctaButton2Link: e.target.value}})} 
                                        />
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <p className="text-muted"><small>Note: The interactive terminal content must be updated using the <strong>Advanced JSON Editor</strong>.</small></p>
                                </div>
                            </>
                        )}
                        
                        <div className="mt-4 d-flex gap-2">
                            <button type="submit" className="btn text-white fw-bold px-5" style={{ background: 'var(--eventflow-primary)' }} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Page Content'}
                            </button>
                            <button type="button" className="btn btn-outline-secondary text-white" onClick={() => setIsEditing(false)}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <>
            {error && <div className="alert alert-danger">{error}</div>}
            
            <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="card-header border-0 bg-transparent py-4 px-4 d-flex justify-content-between align-items-center">
                    <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>CTF Page Content</h4>
                    <button onClick={() => setIsEditing(true)} className="btn btn-sm text-white fw-bold" style={{ background: 'var(--eventflow-primary)' }}>
                        <i className="fa fa-edit me-2"></i>Edit Content
                    </button>
                </div>
                
                <div className="card-body px-4 pb-4 text-white">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="row g-4">
                            <div className="col-12">
                                <div className="p-4 rounded" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <h6 className="text-uppercase text-white-50 mb-3" style={{ fontSize: '12px', letterSpacing: '2px' }}>Hero Section Preview</h6>
                                    <h3>{formData.section?.title} <span style={{ color: 'var(--eventflow-primary)' }}>{formData.section?.titleAccent}</span> {formData.section?.titleEnd}</h3>
                                    <p style={{ color: 'rgba(255,255,255,0.7)' }}>{formData.section?.subtitle}</p>
                                    <div className="d-flex gap-3 mt-3 opacity-75">
                                        <span>📅 {formData.hero?.date}</span>
                                        <span>👥 {formData.hero?.teamSize}</span>
                                        <span>💰 {formData.hero?.prizePool}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="p-4 rounded h-100" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <h6 className="text-uppercase text-white-50 mb-3" style={{ fontSize: '12px', letterSpacing: '2px' }}>Categories ({formData.categories?.length || 0})</h6>
                                    <ul className="list-unstyled m-0">
                                        {(formData.categories || []).map((c, i) => (
                                            <li key={i} className="mb-2 pb-2 border-bottom border-secondary last-border-0">
                                                <i className={`fa ${c.icon} me-2`} style={{ color: 'var(--eventflow-primary)' }}></i>
                                                <strong>{c.title}</strong>
                                                <span className="ms-2 badge bg-dark text-warning border border-warning">{c.points}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="col-md-6">
                                <div className="p-4 rounded h-100" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <h6 className="text-uppercase text-white-50 mb-3" style={{ fontSize: '12px', letterSpacing: '2px' }}>Prizes ({formData.prizes?.length || 0})</h6>
                                    <ul className="list-unstyled m-0">
                                        {(formData.prizes || []).map((p, i) => (
                                            <li key={i} className="mb-2 pb-2 border-bottom border-secondary last-border-0">
                                                <span style={{ color: p.color, width: '30px', display: 'inline-block' }}><i className={`fa ${p.icon}`}></i></span>
                                                <strong className="me-2">{p.rank} Place:</strong>
                                                <span>{p.prize}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
